import { useProductionDeclarationCorrectionRequestCommentCreate } from '@/features/production-declaration/hooks/use-production-declaration-correction-request-comment-create';
import { useProductionDeclarationCorrectionRequestCommentDelete } from '@/features/production-declaration/hooks/use-production-declaration-correction-request-comment-delete';
import { useProductionDeclarationCorrectionRequestCommentEdit } from '@/features/production-declaration/hooks/use-production-declaration-correction-request-comment-edit';
import { useProductionDeclarationCorrectionRequestCommentResolution } from '@/features/production-declaration/hooks/use-production-declaration-correction-request-comment-resolution';
import {
  getProductionDeclarationCorrectionRequestsQueryKey,
  useGetProductionDeclarationCorrectionRequests,
} from '@/services/apis/queries/production-declaration/use-get-production-declaration-correction-requests';
import type { CorrectionRequestEntityType } from '@/types/core/purchase-order/correction-request';

type UseProductionDeclarationCorrectionRequestCommentParams = {
  commodityOriginId: string;
  enabled?: boolean;
  entityType: CorrectionRequestEntityType;
  materialId: string;
  productionDeclarationId: string;
  token?: string;
};

export function useProductionDeclarationCorrectionRequestComment({
  commodityOriginId,
  enabled = true,
  entityType,
  materialId,
  productionDeclarationId,
  token,
}: UseProductionDeclarationCorrectionRequestCommentParams) {
  const commentsQueryParams = {
    productionDeclarationId,
    materialId,
    commodityOriginId,
    entityType,
    token,
  };
  const commentsQueryKey =
    getProductionDeclarationCorrectionRequestsQueryKey(commentsQueryParams);
  const { data: comments = [] } = useGetProductionDeclarationCorrectionRequests(
    {
      ...commentsQueryParams,
      enabled,
    },
  );
  const mutationParams = {
    productionDeclarationId,
    materialId,
    commodityOriginId,
    entityType,
    commentsQueryKey,
  };
  const commentCreate =
    useProductionDeclarationCorrectionRequestCommentCreate(mutationParams);
  const commentEdit =
    useProductionDeclarationCorrectionRequestCommentEdit(mutationParams);
  const commentDelete =
    useProductionDeclarationCorrectionRequestCommentDelete(mutationParams);
  const commentResolution =
    useProductionDeclarationCorrectionRequestCommentResolution(mutationParams);

  return {
    comments,
    create: {
      cancel: commentCreate.handleCreateCancel,
      comment: commentCreate.comment,
      isOpen: commentCreate.isFormOpen,
      isPending: commentCreate.isCreatingComment,
      open: commentCreate.openForm,
      setComment: commentCreate.setComment,
      submit: commentCreate.handleCreateSubmit,
    },
    edit: {
      cancel: commentEdit.handleEditCancel,
      comment: commentEdit.editedComment,
      isOpen: commentEdit.isEditDialogOpen,
      isPending: commentEdit.isUpdatingComment,
      onOpenChange: commentEdit.handleEditDialogOpenChange,
      open: commentEdit.handleEditClick,
      setComment: commentEdit.setEditedComment,
      submit: commentEdit.handleEditSubmit,
    },
    remove: {
      confirm: commentDelete.handleDeleteConfirm,
      isOpen: commentDelete.isDeleteDialogOpen,
      isPending: commentDelete.isDeletingComment,
      onOpenChange: commentDelete.handleDeleteDialogOpenChange,
      open: commentDelete.handleDeleteClick,
    },
    resolution: {
      isPending: commentResolution.isUpdatingCommentResolution,
      toggle: commentResolution.handleCommentResolutionChange,
    },
  };
}
